import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function HomePage() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const [catDropdownOpen, setCatDropdownOpen] = useState(false);
  const catDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (catDropdownRef.current && !catDropdownRef.current.contains(e.target as Node)) {
        setCatDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const langName = (item: typeof categories[0]) => {
    if (currentLang === 'pt') return item.name;
    if (currentLang === 'en') return item.nameEn;
    return item.nameDe;
  };

  const articleTitle = (item: typeof articles[0]) => {
    if (currentLang === 'pt') return item.title;
    if (currentLang === 'en') return item.titleEn;
    return item.titleDe;
  };

  const articleDesc = (item: typeof articles[0]) => {
    if (currentLang === 'pt') return item.description;
    if (currentLang === 'en') return item.descriptionEn;
    return item.descriptionDe;
  };

  const comparisonRows = [
    { label: t('comparison_organizacao'), spreadsheet: t('comparison_alta'), manual: t('comparison_variavel') },
    { label: t('comparison_descoberta'), spreadsheet: t('comparison_simples'), manual: t('comparison_demorada') },
    { label: t('comparison_categorias'), spreadsheet: t('comparison_estruturadas'), manual: t('comparison_dispersas') },
    { label: t('comparison_pesquisa'), spreadsheet: t('comparison_centralizada'), manual: t('comparison_diversas') },
    { label: t('comparison_guias'), spreadsheet: t('comparison_integrados'), manual: t('comparison_separados') },
  ];

  const howSteps = [
    t('how_step_1'),
    t('how_step_2'),
    t('how_step_3'),
    t('how_step_4'),
    t('how_step_5'),
    t('how_step_6'),
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20minimalist%20luxury%20background%20with%20warm%20terracotta%20and%20sage%20green%20gradients%2C%20soft%20organic%20shapes%20and%20subtle%20texture%2C%20premium%20editorial%20aesthetic%2C%20elegant%20bokeh%20light%20particles%2C%20muted%20earth%20tones%2C%20contemporary%20art%20style%20suitable%20for%20e-commerce%20hero%20banner%2C%20high%20resolution&width=1920&height=1080&seq=hero-bg-v3-abstract&orientation=landscape"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/30 via-foreground-950/20 to-foreground-950/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-20 md:py-28 lg:py-36">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-sm font-medium text-white/80 tracking-widest uppercase mb-4">
              {t('hero_eyebrow')}
            </p>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight tracking-tight drop-shadow-sm">
              {t('hero_h1')}
            </h1>
            <p className="mt-5 md:mt-6 text-base md:text-lg text-white/80 leading-relaxed max-w-2xl mx-auto">
              {t('hero_desc')}
            </p>

            {/* Search */}
            <div className="mt-8 md:mt-10 max-w-xl mx-auto relative">
              <div className="relative">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 w-5 h-5 flex items-center justify-center" />
                <input
                  type="text"
                  placeholder={t('hero_search_placeholder')}
                  className="w-full pl-12 pr-4 py-3.5 md:py-4 bg-white/90 backdrop-blur-sm border border-white/20 rounded-xl text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-white/40 transition-all shadow-lg"
                />
              </div>
            </div>

            {/* CTAs */}
            <div className="mt-6 md:mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-6 py-3 md:px-8 md:py-3.5 text-sm font-medium bg-white text-primary-700 rounded-lg hover:bg-white/90 transition-colors shadow-lg whitespace-nowrap"
              >
                {t('hero_cta_primary')}
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-6 py-3 md:px-8 md:py-3.5 text-sm font-medium border border-white/40 text-white bg-white/10 backdrop-blur-sm rounded-lg hover:bg-white/20 transition-colors whitespace-nowrap"
              >
                {t('hero_cta_secondary')}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="border-y border-background-200/70 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 md:py-5">
          <div className="flex flex-wrap items-center justify-center gap-x-6 md:gap-x-10 gap-y-2">
            {[
              t('trust_recursos'),
              t('trust_atualizado'),
              t('trust_guides'),
              t('trust_seguranca'),
            ].map((item) => (
              <div key={item} className="flex items-center gap-2 text-sm text-foreground-500">
                <i className="ri-check-line w-4 h-4 flex items-center justify-center text-primary-500" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section id="categories" className="py-14 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 md:mb-10">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 tracking-tight">{t('categories_h2')}</h2>
              <p className="mt-2 text-sm md:text-base text-foreground-500">{t('categories_desc')}</p>
            </div>

            {/* Dropdown */}
            <div ref={catDropdownRef} className="relative">
              <button
                onClick={() => setCatDropdownOpen(!catDropdownOpen)}
                className="px-4 py-2.5 text-sm font-medium border border-background-200/70 rounded-lg bg-background-50 text-foreground-700 hover:bg-background-100 transition-colors flex items-center gap-2"
              >
                <i className="ri-apps-line w-4 h-4 flex items-center justify-center" />
                {t('nav_categories')}
                <i className={`ri-arrow-down-s-line w-4 h-4 flex items-center justify-center transition-transform ${catDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {catDropdownOpen && (
                <div className="absolute right-0 top-full mt-2 w-64 md:w-72 bg-background-50 border border-background-200/70 rounded-xl shadow-xl overflow-hidden z-40 max-h-[60vh] overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-primary-50 transition-colors group"
                      onClick={() => setCatDropdownOpen(false)}
                    >
                      <div className="w-8 h-8 rounded-md overflow-hidden flex-shrink-0 bg-background-200">
                        <img src={cat.image} alt={langName(cat)} className="w-full h-full object-cover" />
                      </div>
                      <span className="text-sm text-foreground-700 group-hover:text-primary-700">{langName(cat)}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Category Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
            {categories.map((cat) => (
              <a
                key={cat.name}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="group block bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-200/70 hover:shadow-md transition-all"
              >
                <div className="aspect-square overflow-hidden bg-background-100">
                  <img
                    src={cat.image}
                    alt={langName(cat)}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-3 md:p-4">
                  <p className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-1">{langName(cat)}</p>
                </div>
              </a>
            ))}
          </div>

          <div className="mt-8 text-center">
            <a
              href="/products"
              className="inline-block px-6 py-3 text-sm font-medium bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              View All Products
            </a>
          </div>
        </div>
      </section>

      {/* Main CTA Buttons */}
      <section className="py-8 md:py-12 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-8 py-4 text-sm font-semibold bg-accent-500 text-background-50 rounded-xl hover:bg-accent-600 transition-colors shadow-sm whitespace-nowrap"
            >
              {t('cta_cssbuy_spreadsheet')}
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-8 py-4 text-sm font-semibold bg-primary-500 text-background-50 rounded-xl hover:bg-primary-600 transition-colors shadow-sm whitespace-nowrap"
            >
              {t('cta_buy_it_now')}
            </a>
          </div>
        </div>
      </section>

      {/* Articles Section */}
      <section id="articles" className="py-14 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="mb-8 md:mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 tracking-tight">{t('articles_h2')}</h2>
            <p className="mt-2 text-sm md:text-base text-foreground-500">{t('articles_desc')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {articles.map((article) => (
              <a
                key={article.slug}
                href={`/article/${article.slug}`}
                className="group block bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-200/70 hover:shadow-md transition-all"
              >
                <div className="aspect-video overflow-hidden bg-background-100">
                  <img
                    src={article.image}
                    alt={articleTitle(article)}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-4 md:p-5">
                  <div className="flex items-center gap-3 text-xs text-foreground-400 mb-2">
                    <span className="flex items-center gap-1">
                      <i className="ri-calendar-line w-3.5 h-3.5 flex items-center justify-center" />
                      {article.updatedDate}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line w-3.5 h-3.5 flex items-center justify-center" />
                      {article.readingTime} {t('article_reading_time')}
                    </span>
                  </div>
                  <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors leading-snug">
                    {articleTitle(article)}
                  </h3>
                  <p className="mt-2 text-sm text-foreground-500 line-clamp-2 leading-relaxed">
                    {articleDesc(article)}
                  </p>
                  <div className="mt-4 flex items-center gap-1 text-sm font-medium text-primary-600">
                    <span>{t('article_read_guide')}</span>
                    <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-14 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-12">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 tracking-tight">{t('how_it_works_h2')}</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
            {howSteps.map((step, i) => (
              <div key={i} className="relative bg-background-50 border border-background-200/70 rounded-xl p-4 md:p-5 text-center">
                <div className="w-10 h-10 mx-auto rounded-full bg-primary-500 text-background-50 flex items-center justify-center text-sm font-bold mb-3">
                  {i + 1}
                </div>
                <p className="text-sm font-medium text-foreground-700">{step}</p>
                {i < howSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 w-6 text-foreground-300">
                    <i className="ri-arrow-right-line w-5 h-5 flex items-center justify-center" />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-block px-6 py-3 text-sm font-medium bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              {t('how_cta')}
            </a>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 tracking-tight text-center mb-8 md:mb-10">
            {t('comparison_h2')}
          </h2>

          <div className="overflow-x-auto rounded-xl border border-background-200/70">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-background-100">
                  <th className="px-4 py-3 md:px-6 md:py-4 text-left font-semibold text-foreground-700 border-b border-background-200/70">{t('comparison_criterio')}</th>
                  <th className="px-4 py-3 md:px-6 md:py-4 text-left font-semibold text-primary-700 border-b border-background-200/70">{t('comparison_spreadsheet')}</th>
                  <th className="px-4 py-3 md:px-6 md:py-4 text-left font-semibold text-foreground-700 border-b border-background-200/70">{t('comparison_manual')}</th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row, i) => (
                  <tr key={i} className="bg-background-50 hover:bg-background-100/50 transition-colors">
                    <td className="px-4 py-3 md:px-6 md:py-4 text-foreground-700 font-medium border-b border-background-200/50">{row.label}</td>
                    <td className="px-4 py-3 md:px-6 md:py-4 text-foreground-600 border-b border-background-200/50">
                      <span className="inline-flex items-center gap-1.5">
                        <i className="ri-check-line w-4 h-4 flex items-center justify-center text-primary-500" />
                        {row.spreadsheet}
                      </span>
                    </td>
                    <td className="px-4 py-3 md:px-6 md:py-4 text-foreground-500 border-b border-background-200/50">{row.manual}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Review Section */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 tracking-tight">{t('review_h2')}</h2>
              <p className="mt-4 text-sm md:text-base text-foreground-500 leading-relaxed">
                {t('review_desc')}
              </p>
              <a
                href="/article/cssbuy-review"
                className="mt-6 inline-block px-6 py-3 text-sm font-medium bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                {t('review_cta')}
              </a>
            </div>
            <div className="rounded-xl overflow-hidden border border-background-200/70">
              <img
                src="https://readdy.ai/api/search-image?query=Quality%20inspection%20desk%20with%20a%20sneaker%2C%20magnifying%20glass%2C%20checklist%20and%20rating%20cards%20on%20warm%20wooden%20surface%2C%20professional%20QC%20review%20atmosphere%2C%20soft%20window%20light%2C%20warm%20neutral%20tones%2C%20editorial%20photography&width=800&height=500&seq=review-section-v2&orientation=landscape&nocache=true"
                alt="CSSBuy Review"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Shipping + Coupon Sections */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
            {/* Shipping */}
            <div className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/70">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 tracking-tight">{t('shipping_h2')}</h2>
              <p className="mt-3 text-sm md:text-base text-foreground-500 leading-relaxed">
                {t('shipping_desc')}
              </p>
              <a
                href="/article/cssbuy-shipping-cost"
                className="mt-5 inline-block px-5 py-2.5 text-sm font-medium bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                {t('shipping_cta')}
              </a>
            </div>

            {/* Coupon */}
            <div className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/70">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 tracking-tight">{t('coupon_h2')}</h2>
              <p className="mt-3 text-sm md:text-base text-foreground-500 leading-relaxed">
                {t('coupon_desc')}
              </p>
              <a
                href="/article/cssbuy-coupon"
                className="mt-5 inline-block px-5 py-2.5 text-sm font-medium bg-accent-500 text-background-50 rounded-lg hover:bg-accent-600 transition-colors whitespace-nowrap"
              >
                {t('coupon_cta')}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-24 bg-background-100">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 tracking-tight">
            {t('final_cta_h2')}
          </h2>
          <p className="mt-4 md:mt-5 text-sm md:text-base text-foreground-500 leading-relaxed">
            {t('final_cta_desc')}
          </p>
          <div className="mt-7 md:mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-6 py-3 md:px-8 md:py-3.5 text-sm font-medium bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              {t('final_cta_primary')}
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-6 py-3 md:px-8 md:py-3.5 text-sm font-medium border border-background-200/70 text-foreground-700 rounded-lg hover:bg-background-50 transition-colors whitespace-nowrap"
            >
              {t('final_cta_secondary')}
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}